import brandingFields from './fields';

export function deepMerge(objSource, objDestination) {
  // If source is not an object, return it as-is (base case)
  if (typeof objSource !== 'object' || objSource === null) {
    return objSource;
  }

  // If destination is not an object, create a new one
  if (typeof objDestination !== 'object' || objDestination === null) {
    objDestination = Array.isArray(objSource) ? [] : {};
  }

  // Handle arrays - source array replaces destination array
  if (Array.isArray(objSource)) {
    return objSource.map((item) =>
      typeof item === 'object' && item !== null ? deepMerge(item, {}) : item
    );
  }

  // Merge objects recursively
  Object.entries(objSource).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recursively merge nested objects
      objDestination[key] = deepMerge(value, objDestination[key] || {});
    } else if (Array.isArray(value)) {
      // Arrays replace the destination value
      objDestination[key] = value.map((item) =>
        typeof item === 'object' && item !== null ? deepMerge(item, {}) : item
      );
    } else {
      // Primitive values replace the destination value
      objDestination[key] = value;
    }
  });

  return objDestination;
}

let defaultTenantIdentifier = null;
function setDefaultTenantIdentifier(tenantIdentifier) {
  defaultTenantIdentifier = tenantIdentifier;
}

let finalConfigs = {};
function getBrandingConfigs() {
  return JSON.parse(JSON.stringify(finalConfigs));
}
function setBrandingConfigs(configs) {
  finalConfigs = configs;
}
function setBrandingConfigsForTenant(tenantIdentifier, configs) {
  finalConfigs = finalConfigs || {};

  Object.entries(configs).forEach(([group, data]) => {
    finalConfigs[group] = finalConfigs[group] || {};
    finalConfigs[group][tenantIdentifier] = data;
  });
}

function getKnownTenantIdentifiers() {
  return Object.keys(finalConfigs.environments || {});
}

function addTenantIdentifier(tenantIdentifier) {
  finalConfigs.environments = finalConfigs.environments || {};
  finalConfigs.translations = finalConfigs.translations || {};

  finalConfigs.environments[tenantIdentifier] = finalConfigs.environments[tenantIdentifier] || {};
  finalConfigs.translations[tenantIdentifier] = finalConfigs.translations[tenantIdentifier] || {};
}
function removeTenantIdentifier(tenantIdentifier) {
  finalConfigs.environments = finalConfigs.environments || {};
  finalConfigs.translations = finalConfigs.translations || {};
  delete finalConfigs.environments[tenantIdentifier];
  delete finalConfigs.translations[tenantIdentifier];
}

function getTenantIdentifierByDomain(searchDomain) {
  const domainsKey = brandingFields.fields.named.brand.AppBranding.MobileApp.DeepLinking.Domains;

  const knownTenantIdentifiers = getKnownTenantIdentifiers();

  for (const tenantIdentifier of knownTenantIdentifiers) {
    const domains = finalConfigs.environments?.[tenantIdentifier]?.[domainsKey];
    const isMatch = (domains || [])
      .filter(Boolean)
      .map((item) => item.replace(/^https?\:\/\//g, '').replace(/\/.*/g, ''))
      .some((item) => item === searchDomain);

    if (isMatch) {
      return tenantIdentifier;
    }
  }

  return null;
}

function createGetterAssets() {
  const getDomain = (tenantIdentifier) => {
    const domainKey = brandingFields.fields.named.env.AWS.S3.Domain;
    return finalConfigs.environments?.[tenantIdentifier]?.[domainKey];
  };

  const normalize = (prefix, path) => `${prefix}/${path || ''}`.replace(/\/$/g, '');

  const getAssetFor = (tenantIdentifier, path) => {
    return normalize(`${getDomain(tenantIdentifier)}/.branding/assets`, path);
  };
  const getAsset = (path) => {
    return getAssetFor(defaultTenantIdentifier, path);
  };

  return { getAssetFor, getAsset };
}
function createGetterEnvironments() {
  const getFieldFor = (tenantIdentifier, field) => {
    return finalConfigs.environments?.[tenantIdentifier]?.[field];
  };
  const getField = (field) => {
    return getFieldFor(defaultTenantIdentifier, field);
  };

  return { getFieldFor, getField };
}
function createGetterTranslations() {
  const getDataFor = (tenantIdentifier) => {
    return finalConfigs.translations?.[tenantIdentifier];
  };
  const getData = () => {
    return getDataFor(defaultTenantIdentifier);
  };

  return { getDataFor, getData };
}

export {
  getBrandingConfigs,
  setBrandingConfigs,
  setBrandingConfigsForTenant,
  getKnownTenantIdentifiers,
  getTenantIdentifierByDomain,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  deepMerge,
  setDefaultTenantIdentifier,
  getBrandingConfigs,
  setBrandingConfigs,
  setBrandingConfigsForTenant,
  getKnownTenantIdentifiers,
  addTenantIdentifier,
  removeTenantIdentifier,
  getTenantIdentifierByDomain,
  assets: createGetterAssets(),
  environments: createGetterEnvironments(),
  translations: createGetterTranslations(),
};
